"use client";

import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";
import React, { useState } from "react";
import { StarIcon } from "lucide-react";

export interface ProjectCardProps {
  image: string;
  title: string;
  description: string;
  link: string;
  stars?: number | null;
  repo?: string;
}

const ProjectCard = ({ image, title, description, link, stars }: ProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link target="_blank" href={link}>
      <div
        className="group relative flex cursor-pointer flex-col border border-dashed border-border/50 p-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? [0, 1, 0, 1, 0, 1] : 0 }}
          transition={{
            duration: isHovered ? 0.6 : 0.2,
            times: isHovered ? [0, 0.2, 0.4, 0.6, 0.8, 1] : undefined,
            ease: "easeInOut",
          }}
          className="pointer-events-none absolute inset-0 z-10 -m-px border border-dashed border-white/15 bg-muted/15"
        >
          <div className="absolute -left-px -top-px h-2 w-2 border-l border-t border-white/90"></div>
          <div className="absolute -right-px -top-px h-2 w-2 border-r border-t border-white/90"></div>
          <div className="absolute -bottom-px -right-px h-2 w-2 border-b border-r border-white/90"></div>
          <div className="absolute -bottom-px -left-px h-2 w-2 border-b border-l border-white/90"></div>
        </motion.div>
        {/* content */}
        <div className="z-10 h-44 min-h-44 overflow-hidden rounded-sm">
          <Image
            className="h-full object-cover object-top opacity-50 grayscale duration-200 group-hover:opacity-100 group-hover:grayscale-0"
            height="720"
            width="1280"
            src={image}
            alt="image"
          />
        </div>
        <div className="mt-2 flex flex-col gap-1">
          <div className="flex items-center justify-between gap-1.5 text-sm">
            <span>{title}</span>
            {stars && (
              <div className="flex flex-row items-center gap-1">
                <StarIcon className="inline-block size-3 fill-yellow-500 stroke-none" />
                <span className="jetbrains-mono text-xs font-medium">{stars}</span>
              </div>
            )}
          </div>
          <span className="jetbrains-mono line-clamp-2 text-xs tracking-tight text-muted-foreground">
            {description}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ProjectCard;
